import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { ArrowLeft, Cpu, Save, Send, CheckCircle } from 'lucide-react';
import { useUkstrStore, UkstrTask } from '../data/ukstrStore';
import { toast } from 'sonner';
export default function UkstrForm() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    tasks,
    createTask,
    updateTask
  } = useUkstrStore();
  const isNew = !id || id === 'new';
  const existingTask = !isNew ? tasks.find(t => t.id === id) : null;
  const [formData, setFormData] = useState<Partial<UkstrTask>>(existingTask || {
    nomor: '',
    sumber: '',
    pemberi: '',
    klasifikasi: 'RAHASIA',
    tanggal: '',
    batasWaktu: '',
    wilayah: '',
    isuStrategis: '',
    uraian: '',
    lampiran: [],
    judul: '',
    dasar: '',
    sasaran: '',
    eei: '',
    rencana: '',
    analisisRisiko: '',
    mekanisme: '',
    koordinasi: '',
    saran: '',
    otentikasi: '',
    target: []
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const handleChange = (field: keyof UkstrTask, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleGenerate = (section: string) => {
    setIsGenerating(true);
    setTimeout(() => {
      let updates: Partial<UkstrTask> = {};
      if (section === 'all' || section === 'judul') updates.judul = `Operasi Intelijen: ${formData.isuStrategis || 'Deteksi Dini'}`;
      if (section === 'all' || section === 'dasar') updates.dasar = `A. Arahan ${formData.pemberi || 'Pimpinan'}\nB. Situasi terkini di ${formData.wilayah || 'wilayah sasaran'}`;
      if (section === 'all' || section === 'eei') updates.eei = `1. Identifikasi aktor utama\n2. Petakan jaringan dukungan\n3. Analisis kerawanan`;
      if (section === 'all' || section === 'rencana') updates.rencana = `A. HUMINT: Penggalangan sumber tertutup\nB. OSINT: Pemantauan pergerakan digital`;
      if (section === 'all' || section === 'saran') updates.saran = `1. Tingkatkan kewaspadaan\n2. Lakukan operasi cipta kondisi`;
      setFormData(prev => ({
        ...prev,
        ...updates
      }));
      setIsGenerating(false);
      toast.success(`Berhasil meng-generate ${section === 'all' ? 'UK/STR' : section}.`);
    }, 1000);
  };
  const handleSave = () => {
    if (isNew) {
      createTask(formData as Omit<UkstrTask, 'id' | 'status'>);
      toast.success('UK/STR berhasil disimpan sebagai Draft.');
      navigate('/ukstr');
    } else {
      updateTask(id, formData);
      toast.success('Perubahan UK/STR berhasil disimpan.');
    }
  };
  const handlePublish = () => {
    if (isNew) return toast.error('Simpan draft terlebih dahulu.');
    updateTask(id, {
      status: 'Diterbitkan'
    });
    toast.success('UK/STR berhasil diterbitkan ke personel lapangan.');
    navigate('/ukstr');
  };
  return <div className="min-h-full flex flex-col animate-in fade-in duration-500" data-id="element-3889">
      <div className="flex items-center justify-between border-b border-border/50 pb-4 mb-4 shrink-0" data-id="element-3890">
        <div className="flex items-center gap-4" data-id="element-3891">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ukstr')} className="hover:bg-primary/20 hover:text-primary" data-id="element-3892">
            <ArrowLeft className="w-5 h-5" data-id="element-3893" />
          </Button>
          <div data-id="element-3894">
            <h1 className="text-xl font-heading font-bold tracking-tight text-foreground uppercase" data-id="element-3895">
              Form Penjabaran UK/STR
            </h1>
            <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3896">
              {isNew ? 'Buat Tugas Baru' : `Edit Tugas: ${formData.nomor}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-3897">
          <Button variant="outline" onClick={handleSave} className="font-mono text-xs" data-id="element-3898">
            <Save className="w-4 h-4 mr-2" data-id="element-3899" /> Simpan Draft
          </Button>
          {!isNew && formData.status !== 'Diterbitkan' && <Button onClick={handlePublish} className="bg-primary text-primary-foreground font-mono text-xs" data-id="element-3900">
              <Send className="w-4 h-4 mr-2" data-id="element-3901" /> Terbitkan Tugas
            </Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3902">
        {/* PANEL KIRI: INPUT PERINTAH */}
        <Card className="glass-panel border-border/50 flex flex-col overflow-hidden" data-id="element-3903">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 shrink-0" data-id="element-3904">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground" data-id="element-3905">
              Panel Kiri — Input Perintah
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4 flex-1 overflow-y-auto custom-scrollbar" data-id="element-3906">
            <div className="grid grid-cols-2 gap-4" data-id="element-3907">
              <div className="space-y-1" data-id="element-3908">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3909">
                  Nomor Perintah
                </label>
                <input type="text" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.nomor} onChange={e => handleChange('nomor', e.target.value)} data-id="element-3910" />
              </div>
              <div className="space-y-1" data-id="element-3911">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3912">
                  Klasifikasi
                </label>
                <select className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.klasifikasi} onChange={e => handleChange('klasifikasi', e.target.value)} data-id="element-3913">
                  <option data-id="element-3914">RAHASIA</option>
                  <option data-id="element-3915">SANGAT RAHASIA</option>
                  <option data-id="element-3916">TERBATAS</option>
                </select>
              </div>
              <div className="space-y-1" data-id="element-3917">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3918">
                  Sumber Perintah
                </label>
                <input type="text" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.sumber} onChange={e => handleChange('sumber', e.target.value)} data-id="element-3919" />
              </div>
              <div className="space-y-1" data-id="element-3920">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3921">
                  Pemberi Perintah
                </label>
                <input type="text" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.pemberi} onChange={e => handleChange('pemberi', e.target.value)} data-id="element-3922" />
              </div>
              <div className="space-y-1" data-id="element-3923">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3924">
                  Tanggal Perintah
                </label>
                <input type="date" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.tanggal} onChange={e => handleChange('tanggal', e.target.value)} data-id="element-3925" />
              </div>
              <div className="space-y-1" data-id="element-3926">
                <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3927">
                  Batas Waktu
                </label>
                <input type="date" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.batasWaktu} onChange={e => handleChange('batasWaktu', e.target.value)} data-id="element-3928" />
              </div>
            </div>
            <div className="space-y-1" data-id="element-3929">
              <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3930">
                Wilayah Sasaran
              </label>
              <input type="text" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.wilayah} onChange={e => handleChange('wilayah', e.target.value)} data-id="element-3931" />
            </div>
            <div className="space-y-1" data-id="element-3932">
              <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3933">
                Isu Strategis
              </label>
              <input type="text" className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.isuStrategis} onChange={e => handleChange('isuStrategis', e.target.value)} data-id="element-3934" />
            </div>
            <div className="space-y-1" data-id="element-3935">
              <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3936">
                Uraian Perintah
              </label>
              <textarea className="w-full bg-black/40 border border-border/50 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[100px]" value={formData.uraian} onChange={e => handleChange('uraian', e.target.value)} data-id="element-3937" />
            </div>
          </CardContent>
        </Card>

        {/* PANEL KANAN: OUTPUT UK/STR */}
        <Card className="glass-panel border-primary/30 flex flex-col overflow-hidden shadow-[0_0_15px_rgba(var(--primary),0.1)]" data-id="element-3938">
          <CardHeader className="p-4 border-b border-primary/30 bg-primary/5 shrink-0 flex flex-row justify-between items-center" data-id="element-3939">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-primary flex items-center gap-2" data-id="element-3940">
              <Cpu className="w-4 h-4" data-id="element-3941" /> Panel Kanan — Output UK/STR
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 flex flex-col min-h-0" data-id="element-3942">
            <div className="p-3 border-b border-border/50 bg-black/20 grid grid-cols-2 gap-2" data-id="element-3943">
              <Button size="sm" onClick={() => handleGenerate('all')} disabled={isGenerating} className="col-span-2 text-[10px] font-mono h-8 bg-primary text-primary-foreground" data-id="element-3944">
                {isGenerating ? <Cpu className="w-3 h-3 mr-1 animate-spin" data-id="element-3945" /> : <Cpu className="w-3 h-3 mr-1" data-id="element-3946" />}{' '}
                Generate UK/STR Lengkap
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerate('eei')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3947">
                Generate EEI/PIR
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerate('rencana')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3948">
                Generate Rencana Pengumpulan
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerate('saran')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3949">
                Generate Saran Tindak
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleGenerate('all')} disabled={isGenerating} className="text-[9px] font-mono h-7" data-id="element-3950">
                Perbaiki Bahasa
              </Button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto custom-scrollbar flex-1" data-id="element-3951">
              <div className="space-y-1" data-id="element-3952">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3953">
                  Judul UK/STR
                </label>
                <input type="text" className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.judul} onChange={e => handleChange('judul', e.target.value)} data-id="element-3954" />
              </div>
              <div className="space-y-1" data-id="element-3955">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3956">
                  I. Dasar dan Latar Belakang
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[80px]" value={formData.dasar} onChange={e => handleChange('dasar', e.target.value)} data-id="element-3957" />
              </div>
              <div className="space-y-1" data-id="element-3958">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3959">
                  II. Sasaran Penyelidikan
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[60px]" value={formData.sasaran} onChange={e => handleChange('sasaran', e.target.value)} data-id="element-3960" />
              </div>
              <div className="space-y-1" data-id="element-3961">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3962">
                  III. EEI dan PIR
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[80px]" value={formData.eei} onChange={e => handleChange('eei', e.target.value)} data-id="element-3963" />
              </div>
              <div className="space-y-1" data-id="element-3964">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3965">
                  IV. Rencana Pengumpulan
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[60px]" value={formData.rencana} onChange={e => handleChange('rencana', e.target.value)} data-id="element-3966" />
              </div>
              <div className="space-y-1" data-id="element-3967">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3968">
                  V. Analisis Ancaman & Risiko
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[60px]" value={formData.analisisRisiko} onChange={e => handleChange('analisisRisiko', e.target.value)} data-id="element-3969" />
              </div>
              <div className="space-y-1" data-id="element-3970">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3971">
                  VI. Mekanisme Pelaksanaan
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[60px]" value={formData.mekanisme} onChange={e => handleChange('mekanisme', e.target.value)} data-id="element-3972" />
              </div>
              <div className="space-y-1" data-id="element-3973">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3974">
                  VII. Koordinasi dan Pelaporan
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[60px]" value={formData.koordinasi} onChange={e => handleChange('koordinasi', e.target.value)} data-id="element-3975" />
              </div>
              <div className="space-y-1" data-id="element-3976">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3977">
                  VIII. Saran Tindak
                </label>
                <textarea className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none min-h-[60px]" value={formData.saran} onChange={e => handleChange('saran', e.target.value)} data-id="element-3978" />
              </div>
              <div className="space-y-1" data-id="element-3979">
                <label className="text-[10px] font-mono uppercase text-primary" data-id="element-3980">
                  IX. Otentikasi
                </label>
                <input type="text" className="w-full bg-black/20 border border-border/30 rounded p-2 text-xs font-mono focus:border-primary/50 outline-none" value={formData.otentikasi} onChange={e => handleChange('otentikasi', e.target.value)} data-id="element-3981" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}